import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { colorForAccount } from '../charts/palette.js';
import { formatSubName } from '../lib/subName.js';
import { PostDrawer } from '../ui/PostDrawer.js';
import { StarBadge } from '../ui/StarButton.js';
import type { GlobalTopPost, PostChartPoint } from '@wml-crm/shared';

function formatDateRU(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y?.slice(2)}`;
}

function fmtConv(x: number | null): string {
  if (x == null) return '—';
  return `1/${Number.isInteger(x) ? x : x.toFixed(1)}`;
}

function toChartPoint(p: GlobalTopPost): PostChartPoint {
  return {
    postId: p.postId,
    accountId: p.accountId,
    slaveName: p.slaveName,
    handle: p.handle,
    postUrl: p.postUrl,
    postDate: p.postDate,
    lastReportDate: p.lastReportDate,
    views: p.views,
    likes: 0,
    retweets: p.retweets,
    daysInWork: p.daysInWork,
    conversionDay1: p.conversionDay1,
    conversionDay2: null,
    conversionAvg: null,
    reuseCount: p.reuseCount,
  };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function TopDayPage() {
  // null = ещё не знаем последний день с постами (грузим с сервера)
  const [date, setDate] = useState<string | null>(null);
  const [posts, setPosts] = useState<GlobalTopPost[] | null>(null);
  const [selected, setSelected] = useState<PostChartPoint | null>(null);

  // Дефолт — последний день, за который есть выложенные посты.
  useEffect(() => {
    api<{ date: string }>('/api/posts/top-day/latest')
      .then((d) => setDate(d.date || todayIso()))
      .catch(() => setDate(todayIso()));
  }, []);

  useEffect(() => {
    if (!date) return;
    setPosts(null);
    api<GlobalTopPost[]>(`/api/posts/top-day?date=${date}&limit=50`)
      .then(setPosts)
      .catch(() => setPosts([]));
  }, [date]);

  return (
    <div className="home-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em' }}>
            Топ-50 постов за день
          </h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            выложенных {date ? formatDateRU(date) : '…'} · сортировка по итоговым охватам · {posts?.length ?? 0} шт
          </div>
        </div>
        <label className="date-picker" title="Выбрать дату">
          <span>Дата</span>
          <input
            type="date"
            value={date ?? ''}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value || todayIso())}
          />
        </label>
      </div>

      {posts === null ? (
        <div className="empty-state">Загрузка…</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">За этот день постов нет</div>
      ) : (
        <div className="card card-large top-posts">
          <ol className="top-posts-list">
            {posts.map((p, i) => {
              const sub = formatSubName(p.slaveName, p.modelAutoName);
              const color = colorForAccount(p.accountId);
              return (
                <li key={p.postId}>
                  <button
                    className="top-post-row"
                    style={{ gridTemplateColumns: '32px 10px minmax(0, 1fr) 90px 78px 60px 110px 42px 18px' }}
                    onClick={() => setSelected(toChartPoint(p))}
                  >
                    <span className="top-post-rank">{i + 1}</span>
                    <span className="acc-dot" style={{ background: color, borderColor: color }} />
                    <span className="top-post-acc">
                      <b>{p.modelDisplayName}</b>
                      {sub && <span style={{ color: 'var(--text-subtle)', marginLeft: 6 }}>{sub}</span>}
                    </span>
                    <span className="top-post-date">{formatDateRU(p.postDate)}</span>
                    <span className="top-post-days" title="Дней в работе">
                      {p.daysInWork} {p.daysInWork === 1 ? 'день' : (p.daysInWork < 5 ? 'дня' : 'дней')}
                    </span>
                    <span className="top-post-days" title="Конверт день-1">
                      {fmtConv(p.conversionDay1)}
                    </span>
                    <span className="top-post-views">{p.views.toLocaleString('ru-RU')}</span>
                    <StarBadge count={p.reuseCount ?? 0} />
                    <span className="top-post-open" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {selected && (
        <PostDrawer
          point={selected}
          onClose={() => setSelected(null)}
          onReuseChanged={(postId, count) =>
            setPosts((prev) => prev ? prev.map((p) => p.postId === postId ? { ...p, reuseCount: count } : p) : prev)
          }
        />
      )}
    </div>
  );
}
